import React, { useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  fetchAiConfig,
  updateAiConfig,
} from "@/store/slices/aiConfigSlice";
import type { SelectableModel } from "@/services/aiConfigService";
import { EFFORT_LEVELS } from "@/services/aiConfigService";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Save,
  Bot,
  Gauge,
  Wallet,
  Workflow,
  FileText,
  FileCode,
  ShieldCheck,
  RotateCcw,
  ScrollText,
} from "lucide-react";
import { toast } from "sonner";

// Libellé d'une option modèle : "claude-opus-4-8 — 15$ / 75$ par Mtok" ou
// "claude-… — tarif n.c." si le prix est inconnu.
const modelOptionLabel = (m: SelectableModel): string => {
  if (m.inputPrice == null || m.outputPrice == null) {
    return `${m.id} — tarif n.c.`;
  }
  return `${m.id} — ${m.inputPrice}$ / ${m.outputPrice}$ par Mtok`;
};

const buildConfigSchema = (allowedModels: SelectableModel[]) => {
  const ids = allowedModels.map((m) => m.id);
  return Yup.object({
    monthlyBudgetUsd: Yup.number()
      .typeError("Doit être un nombre")
      .positive("Doit être > 0")
      .max(10000, "Trop élevé")
      .required("Requis"),
    globalDirectives: Yup.string().max(4000, "4000 caractères maximum"),
    perRequestBudgetUsd: Yup.number()
      .typeError("Doit être un nombre")
      .positive("Doit être > 0")
      .max(100, "Trop élevé")
      .required("Requis")
      .test(
        "lte-monthly",
        "Doit être <= budget mensuel",
        function (val) {
          return val == null || this.parent.monthlyBudgetUsd == null || val <= this.parent.monthlyBudgetUsd;
        },
      ),
    maxTurnsOrchestrator: Yup.number().integer().positive().required(),
    maxTurnsFormdata: Yup.number().integer().positive().required(),
    maxTurnsSilae: Yup.number().integer().positive().required(),
    maxTurnsValidation: Yup.number().integer().positive().required(),
    modelOrchestrator: Yup.string().oneOf(ids, "Modèle invalide").required(),
    modelFormdata: Yup.string().oneOf(ids, "Modèle invalide").required(),
    modelSilaeScript: Yup.string().oneOf(ids, "Modèle invalide").required(),
    modelValidation: Yup.string().oneOf(ids, "Modèle invalide").required(),
    effortOrchestrator: Yup.string()
      .oneOf(EFFORT_LEVELS as unknown as string[], "Effort invalide")
      .required(),
    effortFormdata: Yup.string()
      .oneOf(EFFORT_LEVELS as unknown as string[], "Effort invalide")
      .required(),
    effortSilaeScript: Yup.string()
      .oneOf(EFFORT_LEVELS as unknown as string[], "Effort invalide")
      .required(),
    effortValidation: Yup.string()
      .oneOf(EFFORT_LEVELS as unknown as string[], "Effort invalide")
      .required(),
  });
};

// Classes reprises de <Input> pour styler les <select> natifs de façon cohérente.
const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

type FieldName =
  | "maxTurnsOrchestrator"
  | "maxTurnsFormdata"
  | "maxTurnsSilae"
  | "maxTurnsValidation"
  | "modelOrchestrator"
  | "modelFormdata"
  | "modelSilaeScript"
  | "modelValidation"
  | "effortOrchestrator"
  | "effortFormdata"
  | "effortSilaeScript"
  | "effortValidation";

interface AgentDescriptor {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  turnsField: FieldName;
  modelField: FieldName;
  effortField: FieldName;
  reserved?: boolean;
}

const AGENTS: AgentDescriptor[] = [
  {
    key: "orchestrator",
    icon: Workflow,
    title: "Orchestrateur",
    description: "Pilote la conversation et délègue aux sous-agents.",
    turnsField: "maxTurnsOrchestrator",
    modelField: "modelOrchestrator",
    effortField: "effortOrchestrator",
  },
  {
    key: "formdata",
    icon: FileText,
    title: "Générateur Mode A",
    description: "Génère les données de formulaire (SilaeFormData).",
    turnsField: "maxTurnsFormdata",
    modelField: "modelFormdata",
    effortField: "effortFormdata",
  },
  {
    key: "silaeScript",
    icon: FileCode,
    title: "Générateur Mode B",
    description: "Génère un script SILAE brut en langage naturel.",
    turnsField: "maxTurnsSilae",
    modelField: "modelSilaeScript",
    effortField: "effortSilaeScript",
  },
  {
    key: "validation",
    icon: ShieldCheck,
    title: "Validateur",
    description: "Valide la syntaxe des scripts SILAE générés.",
    turnsField: "maxTurnsValidation",
    modelField: "modelValidation",
    effortField: "effortValidation",
    reserved: true,
  },
];

export const AiAgentConfigTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { config, allowedModels, loading, saving, error } = useAppSelector(
    (s) => s.aiConfig,
  );

  useEffect(() => {
    dispatch(fetchAiConfig());
  }, [dispatch]);

  if (loading && !config) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (!config) return null;

  const initialValues = {
    monthlyBudgetUsd: config.monthlyBudgetUsd,
    perRequestBudgetUsd: config.perRequestBudgetUsd,
    maxTurnsOrchestrator: config.maxTurnsOrchestrator,
    maxTurnsFormdata: config.maxTurnsFormdata,
    maxTurnsSilae: config.maxTurnsSilae,
    maxTurnsValidation: config.maxTurnsValidation,
    modelOrchestrator: config.modelOrchestrator,
    modelFormdata: config.modelFormdata,
    modelSilaeScript: config.modelSilaeScript,
    modelValidation: config.modelValidation,
    effortOrchestrator: config.effortOrchestrator,
    effortFormdata: config.effortFormdata,
    effortSilaeScript: config.effortSilaeScript,
    effortValidation: config.effortValidation,
    globalDirectives: config.globalDirectives ?? "",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Bot className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold leading-tight">Agent IA</h2>
          <p className="text-sm text-muted-foreground">
            Configuration des limites de coût et des réglages par agent de l'assistant SILAE
          </p>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={buildConfigSchema(allowedModels)}
        enableReinitialize
        onSubmit={async (values) => {
          try {
            await dispatch(
              updateAiConfig({
                ...values,
                globalDirectives: values.globalDirectives.trim() || null,
              }),
            ).unwrap();
            toast.success("Configuration enregistrée");
          } catch (err: any) {
            toast.error(err?.message ?? "Échec de l'enregistrement");
          }
        }}
      >
        {({ values, errors, touched, isValid, dirty, resetForm }) => (
          <Form className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Limites de coût</CardTitle>
                </div>
                <CardDescription>
                  Budget mensuel par utilisateur (USD) et plafond par requête.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="monthlyBudgetUsd">Budget mensuel par utilisateur (USD)</Label>
                  <Field
                    as={Input}
                    id="monthlyBudgetUsd"
                    name="monthlyBudgetUsd"
                    type="number"
                    step="0.01"
                    min="0.01"
                  />
                  {touched.monthlyBudgetUsd && errors.monthlyBudgetUsd && (
                    <p className="text-xs text-destructive">{String(errors.monthlyBudgetUsd)}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="perRequestBudgetUsd">Budget max par requête (USD)</Label>
                  <Field
                    as={Input}
                    id="perRequestBudgetUsd"
                    name="perRequestBudgetUsd"
                    type="number"
                    step="0.01"
                    min="0.01"
                  />
                  {touched.perRequestBudgetUsd && errors.perRequestBudgetUsd && (
                    <p className="text-xs text-destructive">{String(errors.perRequestBudgetUsd)}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div>
              <div className="mb-3 flex items-baseline justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Réglages par agent
                </h3>
                <p className="text-xs text-muted-foreground">
                  Les changements s'appliquent à la requête suivante, sans redémarrage.
                </p>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                {AGENTS.map((agent) => {
                  const Icon = agent.icon;
                  return (
                    <Card key={agent.key} className="relative overflow-hidden">
                      {agent.reserved && (
                        <Badge
                          variant="outline"
                          className="absolute right-4 top-4 gap-1 border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                        >
                          Réservé
                        </Badge>
                      )}
                      <CardHeader>
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                            <Icon className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{agent.title}</CardTitle>
                            <CardDescription className="mt-0.5">
                              {agent.description}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {agent.reserved && (
                          <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                            L'agent de validation n'est pas encore actif au runtime — ces
                            valeurs sont conservées pour cohérence, sans effet immédiat.
                          </p>
                        )}

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <Label htmlFor={agent.turnsField} className="flex items-center gap-1.5 text-xs">
                              <RotateCcw className="h-3 w-3" />
                              Turns max
                            </Label>
                            <Field
                              as={Input}
                              id={agent.turnsField}
                              name={agent.turnsField}
                              type="number"
                              min="1"
                            />
                            {touched[agent.turnsField] && errors[agent.turnsField] && (
                              <p className="text-xs text-destructive">
                                {String(errors[agent.turnsField])}
                              </p>
                            )}
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor={agent.effortField} className="flex items-center gap-1.5 text-xs">
                              <Gauge className="h-3 w-3" />
                              Effort
                            </Label>
                            <Field
                              as="select"
                              id={agent.effortField}
                              name={agent.effortField}
                              className={SELECT_CLASS}
                            >
                              {EFFORT_LEVELS.map((level) => (
                                <option key={level} value={level}>
                                  {level}
                                </option>
                              ))}
                            </Field>
                            {touched[agent.effortField] && errors[agent.effortField] && (
                              <p className="text-xs text-destructive">
                                {String(errors[agent.effortField])}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor={agent.modelField} className="text-xs">
                            Modèle
                          </Label>
                          <Field
                            as="select"
                            id={agent.modelField}
                            name={agent.modelField}
                            className={SELECT_CLASS}
                          >
                            {allowedModels.map((m) => (
                              <option key={m.id} value={m.id}>
                                {modelOptionLabel(m)}
                              </option>
                            ))}
                          </Field>
                          {touched[agent.modelField] && errors[agent.modelField] && (
                            <p className="text-xs text-destructive">
                              {String(errors[agent.modelField])}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ScrollText className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Directives globales</CardTitle>
                </div>
                <CardDescription>
                  Instructions permanentes ajoutées au prompt système de
                  l'assistant pour toutes les demandes de tous les utilisateurs
                  (ton, conventions du cabinet, consignes métier). En cas de
                  contradiction, les règles SILAE (syntaxe, validation)
                  prévalent. Laisser vide pour désactiver.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1.5">
                <Field
                  as={Textarea}
                  id="globalDirectives"
                  name="globalDirectives"
                  rows={6}
                  placeholder={
                    "Exemples :\n- Toujours répondre en français.\n- Préfixer les rubriques créées par le code cabinet.\n- Rappeler de tester en environnement de paie avant mise en production."
                  }
                  className="font-mono text-sm"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-destructive">
                    {touched.globalDirectives && errors.globalDirectives
                      ? String(errors.globalDirectives)
                      : ""}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {values.globalDirectives.length} / 4000
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-end gap-3 pt-2">
              {error && (
                <span className="text-xs text-destructive mr-auto">{error}</span>
              )}
              {dirty && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => resetForm()}
                  disabled={saving}
                >
                  Annuler
                </Button>
              )}
              <Button type="submit" disabled={saving || !isValid} className="gap-2">
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Enregistrer
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
